/**
 * Dynamic Add to Cart / Subscribe button label.
 *
 * Watches the standard Shopify `selling_plan` form field (the field every
 * subscription app, including Loop, must use to actually submit a
 * subscription purchase) and swaps the Add to Cart button's label to match
 * whichever purchase option is currently selected.
 *
 * Anchored on the standard selling_plan form field rather than Loop's own
 * markup, so it keeps working across Loop UI updates and across Horizon's
 * variant/section re-renders (the listener lives on `document`, so it
 * survives the button itself being replaced in the DOM).
 */
(function () {
  'use strict';

  function getLabelWrapper(form) {
    return form ? form.querySelector('[data-dynamic-cart-label]') : null;
  }

  function getSellingPlanValue(form) {
    if (!form) return '';

    // Radio-button style selling plan selector
    var checkedRadio = form.querySelector('input[name="selling_plan"]:checked');
    if (checkedRadio) return checkedRadio.value || '';

    // Hidden-input / select style selling plan field
    var field = form.querySelector('[name="selling_plan"]');
    if (field) return field.value || '';

    return '';
  }

  function suppressHiddenIconText(button) {
    // Horizon's add-to-cart-button snippet includes an
    // aria-hidden="true" "icon wrapper" span that's meant to hold a visual
    // icon only. On this store no icon is rendering into it, so it falls
    // back to showing raw label text — and something (Horizon and/or Loop)
    // updates that text independently of the data-one-time-label /
    // data-subscribe-label attributes this script reads, using different
    // wording ("Add subscription to cart"). The result is two different
    // strings visibly stacked in the button.
    //
    // Since the span is aria-hidden, it's already excluded from what
    // screen readers announce — the sibling text node this script updates
    // is the real accessible label. So it's safe to hide this span
    // visually whenever it contains only stray text and no actual icon
    // graphic, leaving a single, correct, visible label.
    var hiddenEls = button.querySelectorAll('[aria-hidden="true"]');
    hiddenEls.forEach(function (el) {
      if (el.querySelector('svg, img')) return; // has a real icon — leave it alone
      if (el.textContent.trim().length === 0) return; // nothing to hide
      el.style.display = 'none';
    });
  }

  function setButtonText(wrapper, text, otherText) {
    if (!wrapper || !text) return;

    var button = wrapper.querySelector('[data-testid="standalone-add-to-cart"]');
    if (!button) return;

    suppressHiddenIconText(button);

    // Horizon/Loop markup (pre-existing, not caused by this script) can
    // render the button's label in more than one text node at once — e.g.
    // once inside an aria-hidden icon-wrapper span meant to hold only an
    // icon, and once as a separate sibling text node. Updating only the
    // last node found left the other stale copy showing the old label, so
    // instead we walk every non-empty text node in the button and update
    // each one that currently shows either known label (the label we're
    // switching to, or the one we're switching away from). Anything else
    // (e.g. a screen-reader-only string that isn't one of our labels) is
    // left alone.
    var walker = document.createTreeWalker(button, NodeFilter.SHOW_TEXT, {
      acceptNode: function (node) {
        return node.textContent.trim().length > 0
          ? NodeFilter.FILTER_ACCEPT
          : NodeFilter.FILTER_SKIP;
      }
    });

    // First pass: collect every non-empty text node, and note which ones
    // already match one of our two known labels.
    var allNodes = [];
    var labelNodes = [];
    var current;
    while ((current = walker.nextNode())) {
      allNodes.push(current);
      var trimmed = current.textContent.trim();
      if (trimmed === text || trimmed === otherText) {
        labelNodes.push(current);
      }
    }

    if (labelNodes.length > 0) {
      // Normal case: update every copy of the label found — whether it's
      // showing the "other" state's text (needs to flip) or already shows
      // the target text (harmless no-op) — so all duplicate copies stay
      // in sync regardless of how many the theme renders.
      labelNodes.forEach(function (node) {
        var trimmed = node.textContent.trim();
        if (trimmed !== text) {
          node.textContent = node.textContent.replace(trimmed, text);
        }
      });
    } else if (allNodes.length > 0) {
      // Neither label matched any node — likely the very first run, before
      // any label swap has happened, where the button still shows the
      // theme's original default text. Fail safe by updating just the
      // last node rather than guessing at every node in the button.
      var lastNode = allNodes[allNodes.length - 1];
      lastNode.textContent = lastNode.textContent.replace(
        lastNode.textContent.trim(),
        text
      );
    } else {
      // No text node found at all (unexpected markup) — fail safe, don't touch the button.
      if (window.console) {
        console.warn(
          '[dynamic-cart-label] Could not locate a text node inside the Add to cart button. ' +
          'Check that the add-to-cart-button snippet renders visible text.'
        );
      }
    }
  }

  function syncLabel(form) {
    var wrapper = getLabelWrapper(form);
    if (!wrapper) return;

    var sellingPlan = getSellingPlanValue(form);
    var oneTimeLabel = wrapper.getAttribute('data-one-time-label');
    var subscribeLabel = wrapper.getAttribute('data-subscribe-label');

    var isSubscription = sellingPlan !== '' && sellingPlan !== 'null' && sellingPlan !== '0';
    var targetText = isSubscription ? subscribeLabel : oneTimeLabel;
    var otherText = isSubscription ? oneTimeLabel : subscribeLabel;
    setButtonText(wrapper, targetText, otherText);
  }

  function syncAllForms(root) {
    var scope = root || document;
    var forms = scope.querySelectorAll('form[action*="/cart/add"]');
    forms.forEach(function (form) {
      syncLabel(form);
    });
  }

  // Event delegation on document: survives Loop's widget re-rendering its
  // own radio inputs, and survives Horizon replacing the button/section.
  document.addEventListener('change', function (event) {
    var target = event.target;
    if (!target || target.name !== 'selling_plan') return;

    var form = target.closest('form[action*="/cart/add"]');
    if (form) syncLabel(form);
  });

  // Belt-and-suspenders: some subscription apps update the selling_plan
  // value programmatically (not via a user "change" event on the input
  // itself) and instead dispatch their own custom event, or swap the DOM
  // node entirely. A short-interval MutationObserver on each product form
  // catches both cases without needing to know Loop's exact event names.
  function observeForm(form) {
    if (form.__dynamicCartLabelObserved) return;
    form.__dynamicCartLabelObserved = true;

    var debounce;
    var observer = new MutationObserver(function () {
      clearTimeout(debounce);
      debounce = setTimeout(function () {
        syncLabel(form);
      }, 50);
    });

    observer.observe(form, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['checked', 'value', 'selected']
    });
  }

  function init() {
    syncAllForms();
    document.querySelectorAll('form[action*="/cart/add"]').forEach(observeForm);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Re-run after Shopify section re-renders (theme editor, and Horizon's
  // own dynamic section updates where applicable).
  document.addEventListener('shopify:section:load', function (event) {
    syncAllForms(event.target);
    (event.target.querySelectorAll
      ? event.target.querySelectorAll('form[action*="/cart/add"]')
      : []
    ).forEach && event.target.querySelectorAll('form[action*="/cart/add"]').forEach(observeForm);
  });
})();